// 115 — Bitácora UCI Pediátrica (Fase 4, Tanda 7a, ver PLAN.md "FASE 4 —
// spec completa" → "115 — Bitácora UCI Pediátrica"). Migrado del motor
// genérico compartido a un componente propio, mismo criterio que 113/114
// — leído directamente de las páginas 233-234 del PDF fuente, footer real
// confirmado "SNS-MSP/HCU-form.115/2021" (misma familia MSP).
//
// Alcance — página 233 ("BITÁCORA UCI PEDIÁTRICA (1)"): Bloques A (header) +
// B (antropometría) + C (datos de bitácora, diagnóstico, % de quemadura,
// gráfico de constantes, hemodinamia, ingresos, pérdidas, balance
// urinario) — Tanda 7a. Más, de la página 234 ("(2)"), Tanda 9a: localización
// de úlceras/quemaduras (tabla, sin la silueta corporal del papel —
// equivalencia funcional, no facsímil, mismo criterio que el resto del
// motor), escala de Braden-Bergstom pediátrica, escalas de sedación
// (Ramsay, Evans), plan de cuidados generales, exámenes especiales e
// interconsultas — primer uso real de `EscalaClinica` (Tanda 3, sin
// consumidor hasta ahora). El resto de la página 234 (perímetros,
// ventilación de alta frecuencia + convencional, neurológico/Glasgow,
// laboratorio, gasometría, TISS-28, prevención de infecciones,
// procedimientos, escala del dolor en paciente sedado, responsable de
// enfermería) queda para las Tandas 9b/9c.
//
// FHIR: Encounter class=ACUTE + extensión de nivel de cuidado intensivo
// (mecanismo compartido), 1 Condition si el diagnóstico de ingreso trae
// texto (1 solo campo, igual que 114 — no 5 filas como 113), y Talla/Peso
// real crean Observation reusando los LOINC de `vitalSigns.ts` (mismo
// criterio que la antropometría de 113 — a diferencia de 114, 115 no
// tiene un bloque de "constantes vitales de ingreso" con valor único: FC/
// FR/TA/Temperatura solo se registran como puntos del gráfico horario, sin
// un consumidor FHIR discreto hoy). El resto es `Par[]` de respaldo +
// diagrama vectorial del gráfico — ver `bloques113Datos.ts`/
// `bloques114Datos.ts` para el mismo patrón ya aplicado dos veces.
//
// Balance urinario/totales (AM/PM/HS/24h, total ingresos/pérdidas/
// balance/balance acumulado/sangre administrada/balance diálisis): campos
// MANUALES, no calculados — mismo criterio que 113/114 (el papel no deja
// clara la fórmula de signo por categoría, no se inventa el cálculo).
import { resumenSilueta, type DatosGraficoConstantesUciPdf, type DatosSiluetaPdf } from '@logiacare/hce-pdf';
import type { FilaTablaHoraria24h } from './uci/TablaHoraria24h';
import { resumenFilaTablaHoraria24h, horaValorTexto, COLUMNAS_HORARIAS_UCI, type ColumnaHorariaUci, type EstadoTablaHoraria24h } from './uci/TablaHoraria24h';
import { estadoInicialTablaHoraria24h } from './uci/TablaHoraria24h';
import { estadoInicialGraficoEnfermeria, type PuntosGraficoEnfermeria, type SerieGraficoEnfermeria } from './uci/GraficoEnfermeria';
import { calcularTotalEscala, paresDeEscalaClinica, type DefinicionEscalaClinica, type OpcionEscalaClinica, type RespuestasEscalaClinica } from './uci/EscalaClinica';
import { estadoInicialChecklistTurno, paresDeItemsChecklistTurno, type EstadoChecklistTurno, type ItemChecklistTurno } from './uci/ChecklistTurno';
import { estadoInicialTablaConColumnas, resumenFilaTablaConColumnas, type ColumnaTablaConColumnas, type EstadoTablaConColumnas, type FilaTablaConColumnas } from './uci/TablaConColumnas';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import { conCodigoCie } from './camposSimplesDatos';
import { imcTexto, numeroATexto, RANGOS_GLASGOW, sumaFilasHorarias, totalGlasgow } from './calculosClinicosDatos';
import { estadoInicialFilasNombrables, paresDeFilasNombrables, tablaConGruposNombrables, type EstadoFilasNombrables } from './uci/filasNombrables';
import { estadoInicialAlergiasSangre, paresAlergiasSangre, type EstadoAlergiasSangre } from './uci/datosClinicosUci';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

// ---------- B. Antropometría (mismos 5 campos que 113) ----------

export const CAMPOS_ANTROPOMETRIA_115 = ['talla', 'pesoReal', 'imc', 'pesoIdeal', 'superficieCorporal'] as const;
export type CampoAntropometria115 = (typeof CAMPOS_ANTROPOMETRIA_115)[number];

export type EstadoAntropometria115 = Readonly<Record<CampoAntropometria115, string>>;

export function estadoInicialAntropometria115(): EstadoAntropometria115 {
  return Object.fromEntries(CAMPOS_ANTROPOMETRIA_115.map((c) => [c, ''])) as EstadoAntropometria115;
}

function paresDeAntropometria115(t: Traducir, e: EstadoAntropometria115): Par[] {
  // IMC = peso real / talla² — calculado (solo lectura); peso ideal y superficie corporal siguen manuales (sin fórmula en el instructivo).
  return CAMPOS_ANTROPOMETRIA_115.map((c) => ({ pregunta: t(`formularios.f115.antropometria.${c}`), respuesta: c === 'imc' ? imcTexto(e.pesoReal, e.talla) : e[c].trim() }));
}

// ---------- C. Bitácora — datos generales ----------

/** Ola 2 (M2): alergias Sí/No + especificar, grupo sanguíneo y factor Rh separados (instructivo de la bitácora UCI, bloque C). */
export interface EstadoDatosBitacora115 extends EstadoAlergiasSangre {
  readonly servicioProcedencia: string;
  readonly fechaIngresoUci: string;
  readonly fechaActual: string;
  readonly porcentajeQuemadura: string;
}

export function estadoInicialDatosBitacora115(): EstadoDatosBitacora115 {
  return { servicioProcedencia: '', fechaIngresoUci: '', fechaActual: '', porcentajeQuemadura: '', ...estadoInicialAlergiasSangre() };
}

function paresDeDatosBitacora115(t: Traducir, e: EstadoDatosBitacora115): Par[] {
  return [
    { pregunta: t('formularios.f115.datosBitacora.servicioProcedencia'), respuesta: e.servicioProcedencia.trim() },
    { pregunta: t('formularios.f115.datosBitacora.fechaIngresoUci'), respuesta: e.fechaIngresoUci.trim() },
    { pregunta: t('formularios.f115.datosBitacora.fechaActual'), respuesta: e.fechaActual.trim() },
    ...paresAlergiasSangre(t, t('formularios.f115.datosBitacora.alergias'), e),
    { pregunta: t('formularios.f115.datosBitacora.porcentajeQuemadura'), respuesta: e.porcentajeQuemadura.trim() },
  ];
}

// ---------- C. Bitácora — gráfico de constantes (1 sola caja, 5 series) ----------

/**
 * Confirmado leyendo la página 233 del PDF fuente: presión arterial
 * (sistólica/diastólica — el papel las marca con un símbolo de "reloj de
 * arena" combinado, no soportado por los 5 símbolos de `GraficoEnfermeria`;
 * se aproxima con 2 series, mismo criterio que 114), frecuencia cardíaca
 * (guion horizontal en el papel, aproximado con cuadrado), frecuencia
 * respiratoria (símbolo por modo ventilatorio — ○ espontánea, Ø asistida,
 * ● controlada — el papel usa 3 símbolos DISTINTOS, a diferencia de 113
 * donde espontánea/asistida compartían el mismo símbolo) y temperatura
 * (triángulo, escala propia 33-40 — el resto comparte 0-240).
 */
export const SERIES_GRAFICO_CONSTANTES_115: readonly SerieGraficoEnfermeria[] = [
  { clave: 'presionArterialSistolica', etiqueta: 'Presión arterial sistólica', simbolo: 'triangulo', color: '#1f4d47', escala: { min: 0, max: 240 } },
  { clave: 'presionArterialDiastolica', etiqueta: 'Presión arterial diastólica', simbolo: 'circuloHueco', color: '#b5563c', escala: { min: 0, max: 240 } },
  { clave: 'frecuenciaCardiaca', etiqueta: 'Frecuencia cardíaca', simbolo: 'cuadrado', color: '#6f8f6a', escala: { min: 0, max: 240 } },
  {
    clave: 'frecuenciaRespiratoria',
    etiqueta: 'Frecuencia respiratoria',
    simbolo: 'circuloHueco',
    color: '#153733',
    escala: { min: 0, max: 240 },
    variantes: [
      { clave: 'espontanea', etiqueta: 'Espontánea', simbolo: 'circuloHueco' },
      { clave: 'asistida', etiqueta: 'Asistida', simbolo: 'cruz' },
      { clave: 'controlada', etiqueta: 'Controlada', simbolo: 'circulo' },
    ],
  },
  { clave: 'temperatura', etiqueta: 'Temperatura', simbolo: 'triangulo', color: '#8a6d3b', escala: { min: 33, max: 40 } },
];

/** Serializa el gráfico de 115 (1 sola caja, 5 series) a `DatosGraficoConstantesUciPdf` — pura, exportada para test. */
export function diagramaGraficoConstantesUciDeEstado115(puntos: PuntosGraficoEnfermeria): DatosGraficoConstantesUciPdf {
  return {
    series: SERIES_GRAFICO_CONSTANTES_115.map((serie) => ({
      clave: serie.clave,
      etiqueta: serie.etiqueta,
      simbolo: serie.simbolo,
      escalaMin: serie.escala.min,
      escalaMax: serie.escala.max,
      variantes: serie.variantes?.map((v) => ({ clave: v.clave, simbolo: v.simbolo })),
      puntos: COLUMNAS_HORARIAS_UCI.filter((c) => puntos[serie.clave]?.[c] !== undefined).map((c) => ({
        columna: c,
        valor: puntos[serie.clave]![c]!.valor,
        variante: puntos[serie.clave]![c]!.variante,
      })),
    })),
  };
}

// ---------- C. Bitácora — hemodinamia ----------

export const FILAS_HEMODINAMIA_115: readonly FilaTablaHoraria24h[] = [
  { clave: 'tensionArterialMedia', etiqueta: 'Tensión arterial media' },
  { clave: 'presionIntracraneana', etiqueta: 'Presión intracraneana' },
  { clave: 'presionPerfusionCerebral', etiqueta: 'Presión de perfusión cerebral' },
  { clave: 'presionVenosaCentral', etiqueta: 'Presión venosa central' },
  { clave: 'gastoCardiaco', etiqueta: 'Gasto cardíaco' },
  { clave: 'fraccionEyeccion', etiqueta: 'Fracción de eyección' },
  { clave: 'presionCapilarPulmonar', etiqueta: 'Presión capilar pulmonar / aurícula izquierda' },
];

// ---------- C. Bitácora — medicación ----------

// Ola 2 (M2): el papel deja ~12 renglones en blanco para medicación, ~5 para nebulización y ~10 para los "ingresos" libres que van sobre el
// bloque fijo de ingresos (medicación, nutrición parenteral, sangre...) — conteo aproximado sobre el escaneo. Cada renglón lleva NOMBRE + 24
// horas (`uci/filasNombrables.ts`, tope 20). El balance de 115 sigue siendo manual (el papel no da fórmula), así que estos renglones no suman
// solos: la enfermera pasa el volumen a las filas fijas "Ingreso parcial/total".

export interface GruposNombrables115 {
  readonly medicacion: EstadoFilasNombrables;
  readonly nebulizacion: EstadoFilasNombrables;
  readonly ingresosLibres: EstadoFilasNombrables;
}

export function estadoInicialGruposNombrables115(): GruposNombrables115 {
  return { medicacion: estadoInicialFilasNombrables(4), nebulizacion: estadoInicialFilasNombrables(2), ingresosLibres: estadoInicialFilasNombrables(4) };
}

// ---------- C. Bitácora — ingresos ----------

export const FILAS_INGRESOS_115: readonly FilaTablaHoraria24h[] = [
  { clave: 'medicacionVolumen', etiqueta: 'Medicación', unidad: 'mL' },
  { clave: 'nutricionParenteralTotal', etiqueta: 'Nutrición parenteral total', unidad: 'mL' },
  { clave: 'sangre', etiqueta: 'Sangre', unidad: 'mL' },
  { clave: 'plasma', etiqueta: 'Plasma', unidad: 'mL' },
  { clave: 'plaquetas', etiqueta: 'Plaquetas', unidad: 'mL' },
  { clave: 'crioprecipitados', etiqueta: 'Crioprecipitados', unidad: 'mL' },
  { clave: 'dieta', etiqueta: 'Dieta', unidad: 'mL' },
  { clave: 'aguaEndogena', etiqueta: 'Agua endógena', unidad: 'mL' },
  { clave: 'ingresoParcial', etiqueta: 'Ingreso parcial', unidad: 'mL' },
  { clave: 'ingresoTotal', etiqueta: 'Ingreso total', unidad: 'mL' },
];

// ---------- C. Bitácora — pérdidas ----------

export const FILAS_PERDIDAS_115: readonly FilaTablaHoraria24h[] = [
  { clave: 'insensible', etiqueta: 'Insensible', unidad: 'mL' },
  { clave: 'vomito', etiqueta: 'Vómito', unidad: 'mL' },
  { clave: 'sondaNasogastrica', etiqueta: 'Sonda nasogástrica', unidad: 'mL' },
  { clave: 'drenaje', etiqueta: 'Drenaje', unidad: 'mL' },
  { clave: 'drenajeToracico', etiqueta: 'Drenaje torácico', unidad: 'mL' },
  { clave: 'drenajeTuboRetroexternal', etiqueta: 'Drenaje T. retroexternal', unidad: 'mL' },
  { clave: 'drenajeAbdominal', etiqueta: 'Drenaje abdominal', unidad: 'mL' },
  { clave: 'hecesColostoma', etiqueta: 'Heces / colostoma', unidad: 'mL' },
  { clave: 'diuresisHorario', etiqueta: 'Diuresis horario', unidad: 'mL' },
  { clave: 'diuresisTotal', etiqueta: 'Diuresis total', unidad: 'mL' },
  { clave: 'dialisis', etiqueta: 'Diálisis', unidad: 'mL' },
  { clave: 'perdidaParcial', etiqueta: 'Pérdida parcial', unidad: 'mL' },
  { clave: 'balanceTotal', etiqueta: 'Balance total', unidad: 'mL' },
];

// ---------- C. Bitácora — balance urinario + totales (campos manuales) ----------

export const CAMPOS_BALANCE_URINARIO_115 = ['am', 'pm', 'hs', 'horas24'] as const;
export type CampoBalanceUrinario115 = (typeof CAMPOS_BALANCE_URINARIO_115)[number];
export type EstadoBalanceUrinario115 = Readonly<Record<CampoBalanceUrinario115, string>>;

export const CAMPOS_TOTALES_115 = ['totalIngresos', 'totalPerdidas', 'balance', 'balanceAcumulado', 'totalSangreAdministrada', 'balanceDialisis'] as const;
export type CampoTotales115 = (typeof CAMPOS_TOTALES_115)[number];
export type EstadoTotales115 = Readonly<Record<CampoTotales115, string>>;

export function estadoInicialBalanceUrinario115(): EstadoBalanceUrinario115 {
  return Object.fromEntries(CAMPOS_BALANCE_URINARIO_115.map((c) => [c, ''])) as EstadoBalanceUrinario115;
}

export function estadoInicialTotales115(): EstadoTotales115 {
  return Object.fromEntries(CAMPOS_TOTALES_115.map((c) => [c, ''])) as EstadoTotales115;
}

function paresDeBalanceUrinario115(t: Traducir, e: EstadoBalanceUrinario115): Par[] {
  return CAMPOS_BALANCE_URINARIO_115.map((c) => ({ pregunta: t(`formularios.f115.balanceUrinario.${c}`), respuesta: e[c].trim() }));
}

function paresDeTotales115(t: Traducir, e: EstadoTotales115): Par[] {
  return CAMPOS_TOTALES_115.map((c) => ({ pregunta: t(`formularios.f115.totales.${c}`), respuesta: e[c].trim() }));
}

// ---------- C. Bitácora — TOTAL DÍA (Ola 2) ----------
// El papel imprime una casilla "TOTAL DÍA" a la derecha de "Ingreso total" y otra a la derecha de "Balance total" de las pérdidas (pág. 233); el
// instructivo (pág. 237-238) no la define. Lectura del papel: el bloque vertical "INGRESOS" abarca los renglones libres (sobre "Medicación") y
// las 8 filas fijas (Medicación ... Agua endógena) que "Ingreso parcial"/"Ingreso total" resumen; "MEDICACIÓN" y "NEBULIZACIÓN" son bloques
// APARTE (el instructivo pide "la medicación indicada y el horario" y "si requiere nebulización y el horario": es horario/dosis, no volumen, y el
// volumen de la medicación IV ya va en la fila fija "Medicación"; sumarlos duplicaría). Decisión:
//  - TOTAL DÍA de ingresos = suma de las 24 horas de los renglones libres de INGRESOS + las 8 filas fijas de volumen. Si no hay ningún volumen
//    en esas filas, se usa la suma de la fila "Ingreso parcial" (la enfermera que solo anota el parcial). "Ingreso total" nunca se suma: puede
//    ser el acumulado y duplicaría.
//  - TOTAL DÍA de pérdidas = suma de la fila "Pérdida parcial" (no hay renglones libres en pérdidas; "Balance total" es el balance, no un volumen).
// Es editable: lo que la enfermera escribe manda sobre el cálculo ("Escribir a mano"); vaciarlo vuelve al cálculo.

export const CAMPOS_TOTAL_DIA_115 = ['ingresos', 'perdidas'] as const;
export type CampoTotalDia115 = (typeof CAMPOS_TOTAL_DIA_115)[number];
/** Lo que la enfermera sobrescribió (`''` = usar el cálculo). */
export type EstadoTotalDia115 = Readonly<Record<CampoTotalDia115, string>>;

export function estadoInicialTotalDia115(): EstadoTotalDia115 {
  return { ingresos: '', perdidas: '' };
}

/** Filas fijas de INGRESOS que son un volumen por hora (todas menos las que resumen: "Ingreso parcial" e "Ingreso total"). */
export const FILAS_COMPONENTES_INGRESO_115: readonly string[] = FILAS_INGRESOS_115.map((f) => f.clave).filter((c) => c !== 'ingresoParcial' && c !== 'ingresoTotal');

/**
 * Valor que se ve e imprime de cada TOTAL DÍA: lo escrito a mano o, si no, el cálculo descrito arriba. Pura, exportada para test.
 * `ingresosLibres` = renglones libres del bloque INGRESOS (`GruposNombrables115.ingresosLibres`).
 */
export function totalDiaEfectivo115(
  manual: EstadoTotalDia115,
  ingresos: EstadoTablaHoraria24h,
  perdidas: EstadoTablaHoraria24h,
  ingresosLibres: EstadoFilasNombrables = [],
): EstadoTotalDia115 {
  const texto = (n: number | undefined): string => (n === undefined ? '' : numeroATexto(n));
  const componentes = sumaFilasHorarias(tablaConGruposNombrables(ingresos, { ingresosLibres }), [...FILAS_COMPONENTES_INGRESO_115, 'ingresosLibres']);
  return {
    ingresos: manual.ingresos.trim() || texto(componentes ?? sumaFilasHorarias(ingresos, ['ingresoParcial'])),
    perdidas: manual.perdidas.trim() || texto(sumaFilasHorarias(perdidas, ['perdidaParcial'])),
  };
}

function paresDeTotalDia115(t: Traducir, e: EstadoTotalDia115, ingresos: EstadoTablaHoraria24h, perdidas: EstadoTablaHoraria24h, ingresosLibres: EstadoFilasNombrables): Par[] {
  const efectivo = totalDiaEfectivo115(e, ingresos, perdidas, ingresosLibres);
  return CAMPOS_TOTAL_DIA_115.map((c) => ({ pregunta: t(`formularios.f115.totalDia.${c}`), respuesta: efectivo[c] }));
}

// ---------- Localización de las úlceras por presión o quemadura (Tanda 9a) ----------
// Tabla de ~5 renglones + la silueta corporal (frente/dorso) del papel. Ola 2 (M2): la silueta se captura con `SiluetaCorporal` (la del 003,
// misma geometría que dibuja el PDF firmado); las regiones marcadas salen en el `Par[]` y se dibujan en el PDF (`siluetaCorporal` de
// `onGuardado`). La tabla sigue dando el detalle de cada lesión (trae, fecha de inicio, localización, grado).

/** Regiones marcadas en la silueta (claves `<vista>:<región>`) → diagrama para el PDF firmado; `undefined` si no se marcó nada. */
export function siluetaDeUlceras115(regiones: readonly string[]): DatosSiluetaPdf | undefined {
  return regiones.length > 0 ? { regiones: [...regiones] } : undefined;
}

export const NUM_FILAS_ULCERAS_115 = 5; // el papel trae ~5 renglones (antes 4)

export const FILAS_ULCERAS_115: readonly FilaTablaConColumnas[] = Array.from({ length: NUM_FILAS_ULCERAS_115 }, (_, i) => ({
  clave: `ulcera${i + 1}`,
  etiqueta: String(i + 1),
}));

export const COLUMNAS_ULCERAS_115: readonly ColumnaTablaConColumnas[] = [
  { clave: 'trae', etiqueta: 'Trae' },
  { clave: 'fechaInicio', etiqueta: 'Fecha de inicio', tipo: 'fecha' },
  { clave: 'localizacion', etiqueta: 'Localización' },
  { clave: 'grado', etiqueta: 'Grado' },
];

// ---------- Escala de Braden-Bergstom pediátrica (Tanda 9a) ----------
// Primer consumidor real de `EscalaClinica` (Tanda 3, sin conectar hasta
// ahora). Interpretación transcrita VERBATIM del papel, incluido un hueco
// real en la banda de 13 puntos ("Menor a 13" / "De 14 a 14" / "Mayor a
// 14" — sic, el propio PDF fuente deja 13 sin cubrir; no se inventa un
// cierre de rango que el papel no da).

export const DEFINICION_BRADEN_BERGSTOM_115: DefinicionEscalaClinica = {
  items: [
    {
      clave: 'percepcionSensorial',
      etiqueta: 'Percepción sensorial',
      opciones: [
        { valor: 1, etiqueta: 'Completamente limitada' },
        { valor: 2, etiqueta: 'Muy limitada' },
        { valor: 3, etiqueta: 'Levemente limitada' },
        { valor: 4, etiqueta: 'Sin limitaciones' },
      ],
    },
    {
      clave: 'exposicionHumedad',
      etiqueta: 'Exposición a la humedad',
      opciones: [
        { valor: 1, etiqueta: 'Constantemente húmeda' },
        { valor: 2, etiqueta: 'Con frecuencia húmeda' },
        { valor: 3, etiqueta: 'Ocasionalmente húmeda' },
        { valor: 4, etiqueta: 'Raramente húmeda' },
      ],
    },
    {
      clave: 'actividad',
      etiqueta: 'Actividad',
      opciones: [
        { valor: 1, etiqueta: 'Encamado' },
        { valor: 2, etiqueta: 'En silla' },
        { valor: 3, etiqueta: 'Deambula ocasionalmente' },
        { valor: 4, etiqueta: 'Deambula frecuentemente' },
      ],
    },
    {
      clave: 'movilidad',
      etiqueta: 'Movilidad',
      opciones: [
        { valor: 1, etiqueta: 'Completa inmóvil' },
        { valor: 2, etiqueta: 'Muy limitada' },
        { valor: 3, etiqueta: 'Levemente limitada' },
        { valor: 4, etiqueta: 'Sin limitaciones' },
      ],
    },
    {
      clave: 'nutricion',
      etiqueta: 'Nutrición',
      opciones: [
        { valor: 1, etiqueta: 'Completamente inadecuada' },
        { valor: 2, etiqueta: 'Probablemente inadecuada' },
        { valor: 3, etiqueta: 'Adecuada' },
        { valor: 4, etiqueta: 'Excelente' },
      ],
    },
    {
      clave: 'friccionRoce',
      etiqueta: 'Fricción y roce',
      opciones: [
        { valor: 1, etiqueta: 'Presente' },
        { valor: 2, etiqueta: 'Potencialmente presente' },
        { valor: 3, etiqueta: 'Ausente' },
      ],
    },
  ],
  rangos: [
    { min: 6, max: 12, etiqueta: 'Alto riesgo' },
    { min: 14, max: 14, etiqueta: 'Riesgo' },
    { min: 15, max: 23, etiqueta: 'Bajo riesgo' },
  ],
};

// ---------- Escalas de sedación — Ramsay (Tanda 9a) ----------

export const DEFINICION_RAMSAY_115: DefinicionEscalaClinica = {
  items: [
    {
      clave: 'nivel',
      etiqueta: 'Nivel',
      opciones: [
        { valor: 1, etiqueta: 'Ansioso y/o agitado' },
        { valor: 2, etiqueta: 'Colaborador, tranquilo y orientado. Apertura espontánea de ojos. Somnoliento.' },
        { valor: 3, etiqueta: 'Responde a estímulos verbales' },
        { valor: 4, etiqueta: 'Quieto, ojos cerrados. Rápida respuesta a ligeros golpecitos o verbales fuertes.' },
        { valor: 5, etiqueta: 'Respuesta lenta, sólo responde a estímulos importantes.' },
        { valor: 6, etiqueta: 'No responde' },
      ],
    },
  ],
  rangos: [
    { min: 1, max: 3, etiqueta: 'Paciente despierto' },
    { min: 4, max: 6, etiqueta: 'Paciente dormido' },
  ],
};

// ---------- Escalas de sedación — Evans (Tanda 9a) ----------

export const DEFINICION_EVANS_115: DefinicionEscalaClinica = {
  items: [
    {
      clave: 'tensionSistolica',
      etiqueta: 'Tensión sistólica',
      opciones: [
        { valor: 0, etiqueta: '< 15%' },
        { valor: 1, etiqueta: '15 - 30%' },
        { valor: 2, etiqueta: '> 30%' },
      ],
    },
    {
      clave: 'frecuenciaCardiaca',
      etiqueta: 'Frecuencia cardíaca',
      opciones: [
        { valor: 0, etiqueta: '< 15%' },
        { valor: 1, etiqueta: '15 - 30%' },
        { valor: 2, etiqueta: '> 30%' },
      ],
    },
    {
      clave: 'sudoracion',
      etiqueta: 'Sudoración',
      opciones: [
        { valor: 0, etiqueta: 'Ausente' },
        { valor: 1, etiqueta: 'Piel mojada al tacto' },
        { valor: 2, etiqueta: 'Gotas visibles' },
      ],
    },
    {
      clave: 'lagrimas',
      etiqueta: 'Lágrimas',
      opciones: [
        { valor: 0, etiqueta: 'No con ojos abiertos' },
        { valor: 1, etiqueta: 'Ojos abiertos húmedos' },
        { valor: 2, etiqueta: 'Refluyen con ojos cerrados' },
      ],
    },
  ],
  rangos: [
    { min: 0, max: 2, etiqueta: 'Mantener' },
    { min: 3, max: 5, etiqueta: 'Valorar' },
    { min: 6, max: 8, etiqueta: 'Aumentar sedación o analgesia' },
  ],
};

// ---------- Plan de cuidados generales (Tanda 9a) ----------
// Checklist frecuencia+AM/PM/HS — "Nombre del profesional y firma" del
// papel NO se transcribe: mismo criterio que el bloque K de 113 (ya lo
// cubre el mecanismo real de firma electrónica).

export const ITEMS_PLAN_CUIDADOS_GENERALES_115: readonly ItemChecklistTurno[] = [
  { clave: 'posicion', etiqueta: 'Posición' },
  { clave: 'aislamiento', etiqueta: 'Aislamiento' },
  { clave: 'bano', etiqueta: 'Baño' },
  { clave: 'cambiosPosicion', etiqueta: 'Cambios de posición' },
  { clave: 'cuidadosPiel', etiqueta: 'Cuidados de la piel' },
  { clave: 'cuidadosOjos', etiqueta: 'Cuidados de los ojos' },
  { clave: 'higieneOral', etiqueta: 'Higiene oral' },
  { clave: 'controlTemperaturaMediosFisicos', etiqueta: 'Control de T° por medios físicos' },
  { clave: 'aspiracionSecreciones', etiqueta: 'Aspiración de secreciones' },
  { clave: 'fisioterapiaRespiratoria', etiqueta: 'Fisioterapia respiratoria' },
  { clave: 'fisioterapiaFisica', etiqueta: 'Fisioterapia física' },
  { clave: 'visitaFamilia', etiqueta: 'Visita de la familia' },
  { clave: 'dieta', etiqueta: 'Dieta' },
];

// ---------- Exámenes especiales (Tanda 9a) ----------

export const NUM_FILAS_EXAMENES_ESPECIALES_115 = 9; // el papel trae ~9 renglones (antes 6)

export const FILAS_EXAMENES_ESPECIALES_115: readonly FilaTablaConColumnas[] = Array.from({ length: NUM_FILAS_EXAMENES_ESPECIALES_115 }, (_, i) => ({
  clave: `examen${i + 1}`,
  etiqueta: String(i + 1),
}));

export const COLUMNAS_EXAMENES_ESPECIALES_115: readonly ColumnaTablaConColumnas[] = [
  { clave: 'nombre', etiqueta: 'Exámenes especiales' },
  { clave: 'fecha', etiqueta: 'Fecha', tipo: 'fecha' },
  { clave: 'pendiente', etiqueta: 'Pendiente' },
];

// ---------- Interconsultas (Tanda 9a) ----------

export const NUM_FILAS_INTERCONSULTAS_115 = 4; // el papel trae ~4 renglones (antes 3)

export const FILAS_INTERCONSULTAS_115: readonly FilaTablaConColumnas[] = Array.from({ length: NUM_FILAS_INTERCONSULTAS_115 }, (_, i) => ({
  clave: `interconsulta${i + 1}`,
  etiqueta: String(i + 1),
}));

export const COLUMNAS_INTERCONSULTAS_115: readonly ColumnaTablaConColumnas[] = [
  { clave: 'servicio', etiqueta: 'Servicio interconsultado' },
  { clave: 'horaSolicitud', etiqueta: 'Hora solicitud' },
  { clave: 'horaRespuesta', etiqueta: 'Hora respuesta' },
];

// ---------- Ventilación de alta frecuencia (Tanda 9b) ----------
// Leída de la página 234 del PDF fuente — perímetros + tipo de secreciones
// comparten el mismo bloque horario que los parámetros propios de alta
// frecuencia (hertz/stroke-delta/amplitud/mean press).

export const FILAS_VENTILACION_ALTA_FRECUENCIA_115: readonly FilaTablaHoraria24h[] = [
  { clave: 'perimetroAbdominal', etiqueta: 'Perímetro abdominal' },
  { clave: 'perimetroCefalico', etiqueta: 'Perímetro cefálico' },
  { clave: 'tipoSecrecionesTraqueal', etiqueta: 'Tipo de secreciones (traqueal)' },
  { clave: 'tipoSecrecionesBucal', etiqueta: 'Tipo de secreciones (bucal)' },
  { clave: 'frecuenciaHertz', etiqueta: 'Frecuencia (Hertz)' },
  { clave: 'strokeDelta', etiqueta: 'Stroke / Delta' },
  { clave: 'amplitud', etiqueta: 'Amplitud' },
  { clave: 'meanPress', etiqueta: 'Mean press' },
];

// ---------- Ventilación convencional (Tanda 9b) ----------

export const FILAS_VENTILACION_CONVENCIONAL_115: readonly FilaTablaHoraria24h[] = [
  { clave: 'tuboEndotraqueal', etiqueta: 'Tubo endotraqueal' },
  { clave: 'tipoVentilacion', etiqueta: 'Tipo de ventilación' },
  { clave: 'volumenCorriente', etiqueta: 'Volumen corriente' },
  { clave: 'frecuenciaRespiratoria', etiqueta: 'Frecuencia respiratoria' },
  { clave: 'presionInspiratoriaMaxima', etiqueta: 'Presión inspiratoria máxima' },
  { clave: 'presionPositivaFinalEspiracion', etiqueta: 'Presión positiva al final de la espiración' },
  { clave: 'fraccionInspiradaOxigeno', etiqueta: 'Fracción inspirada de oxígeno' },
  { clave: 'saturacionOxigeno', etiqueta: 'Saturación de oxígeno' },
  { clave: 'dioxidoCarbonoTotal', etiqueta: 'Dióxido de carbono total' },
  { clave: 'inhalacionExhalacion', etiqueta: 'Inhalación / exhalación' },
  { clave: 'flujoO2', etiqueta: 'Flujo O2' },
  { clave: 'tipo', etiqueta: 'Tipo' },
];

// ---------- Neurológico / Glasgow (Tanda 9b) ----------
// Total Glasgow calculado en código (suma de los 3 componentes por hora),
// nunca campo libre — mismo mecanismo exacto que `totalGlasgowPorHora` de
// 113 (`bloques113Datos.ts`).

export const FILAS_NEUROLOGICO_115: readonly FilaTablaHoraria24h[] = [
  { clave: 'aperturaOcular', etiqueta: 'Apertura ocular (4)', rango: RANGOS_GLASGOW.ocular },
  { clave: 'respuestaMotora', etiqueta: 'Respuesta motora (6)', rango: RANGOS_GLASGOW.motor },
  { clave: 'respuestaVerbal', etiqueta: 'Respuesta verbal (5)', rango: RANGOS_GLASGOW.verbal },
];

/**
 * Total Glasgow (apertura ocular + respuesta motora + respuesta verbal) por hora — `undefined` si falta cualquier componente o alguno no es un
 * entero dentro de su rango (ocular 1-4, verbal 1-5, motora 1-6; la celda fuera de rango se marca en pantalla pero el guardado no se bloquea).
 * Pura, exportada para test.
 */
export function totalGlasgowPorHora115(neurologico: EstadoTablaHoraria24h): Partial<Record<ColumnaHorariaUci, number>> {
  const total: Partial<Record<ColumnaHorariaUci, number>> = {};
  for (const columna of COLUMNAS_HORARIAS_UCI) {
    const suma = totalGlasgow(neurologico.aperturaOcular?.[columna], neurologico.respuestaVerbal?.[columna], neurologico.respuestaMotora?.[columna]);
    if (suma !== null) total[columna] = suma;
  }
  return total;
}

/** Resumen compacto "10 h: total · 11 h: total" del Glasgow calculado. */
export function resumenTotalGlasgowPorHora115(neurologico: EstadoTablaHoraria24h): string {
  const total = totalGlasgowPorHora115(neurologico);
  return COLUMNAS_HORARIAS_UCI.filter((c) => total[c] !== undefined)
    .map((c) => horaValorTexto(c, String(total[c])))
    .join(' · ');
}

// ---------- Pupilas (Tanda 9b) ----------
// El papel dibuja una escala visual de tamaño (círculos 1-8mm) por ojo +
// una selección Normal/Lenta reacción/Fija — se aproxima con 2 campos por
// ojo (tamaño en mm + tipo de reacción) en vez de construir un selector
// gráfico de tamaños nuevo: equivalencia funcional, no facsímil, mismo
// criterio que el resto del motor.

export const RANGO_TAMANO_PUPILA_MM_115 = { min: 1, max: 8 } as const;

/**
 * Ola 2 (M2): el papel registra las pupilas HORA POR HORA (una fila de pupilas dibujadas por ojo en las 24 columnas, más las filas Normal /
 * Lenta / Fija de la reacción — pág. 234); antes se guardaba un solo valor por ojo. Ahora son 4 filas horarias: tamaño (1-8 mm, la escala del
 * papel) y reacción por ojo. La reacción se anota con la inicial que trae el papel: N = normal, L = lenta, F = fija.
 */
export const FILAS_PUPILAS_115: readonly FilaTablaHoraria24h[] = [
  { clave: 'izquierdaTamano', etiqueta: 'Pupila izquierda — tamaño (mm)', rango: RANGO_TAMANO_PUPILA_MM_115 },
  { clave: 'izquierdaReaccion', etiqueta: 'Pupila izquierda — reacción (N normal · L lenta · F fija)' },
  { clave: 'derechaTamano', etiqueta: 'Pupila derecha — tamaño (mm)', rango: RANGO_TAMANO_PUPILA_MM_115 },
  { clave: 'derechaReaccion', etiqueta: 'Pupila derecha — reacción (N normal · L lenta · F fija)' },
];

// ---------- Laboratorio (Tanda 9b) ----------

export const FILAS_LABORATORIO_115: readonly FilaTablaHoraria24h[] = [
  { clave: 'glicemiaTest', etiqueta: 'Glicemia test' },
  { clave: 'densidadUrinaria', etiqueta: 'Densidad urinaria' },
];

// ---------- Gasometría (Tanda 9b) ----------

export const FILAS_GASOMETRIA_115: readonly FilaTablaHoraria24h[] = [
  { clave: 'ph', etiqueta: 'pH' },
  { clave: 'pco2', etiqueta: 'PCO2' },
  { clave: 'po2', etiqueta: 'PO2' },
  { clave: 'satO2', etiqueta: 'Sat O2' },
  { clave: 'excesoBase', etiqueta: 'Exceso de base' },
  { clave: 'na', etiqueta: 'Na' },
  { clave: 'ca', etiqueta: 'Ca' },
  { clave: 'k', etiqueta: 'K' },
  { clave: 'hematocrito', etiqueta: 'Hematocrito' },
];

// ---------- Evaluación del TISS-28 (Tanda 9c) ----------
// Leída de la página 234 del PDF fuente — 7 grupos con puntaje FIJO por
// ítem (impreso en el propio papel), igual que Glasgow: aritmética médica
// estándar, sin ambigüedad. Cada ítem se modela como una `EscalaClinica`
// de 2 opciones (No=0 / Sí=puntaje fijo) — mismo componente que
// Braden-Bergstom/Ramsay/Evans, sin inventar un componente nuevo solo para
// esto. El total por grupo (`calcularTotalEscala`) reproduce el
// "SUBTOTAL" que el propio papel imprime por grupo.

function opcionesSiNo(puntos: number): readonly OpcionEscalaClinica[] {
  return [
    { valor: 0, etiqueta: 'No' },
    { valor: puntos, etiqueta: 'Sí' },
  ];
}

export const DEFINICION_TISS_ACTIVIDADES_BASICAS_115: DefinicionEscalaClinica = {
  items: [
    { clave: 'monitorizacionEstandar', etiqueta: 'Monitorización estándar (signos vitales horarios, registros y cálculo de balance de líquido)', opciones: opcionesSiNo(5) },
    { clave: 'multiplesMedicamentosIv', etiqueta: 'Múltiples medicamentos vía intravenosa (más de una droga, bolos o infusión contínua)', opciones: opcionesSiNo(4) },
    { clave: 'cuidadosDrenajes', etiqueta: 'Cuidados de drenajes, cualquiera excepto sonda nasogástrica', opciones: opcionesSiNo(4) },
    { clave: 'viaUnicaMedicamentos', etiqueta: 'Vía única de administración de medicamentos (intravenosa, intramuscular, oral)', opciones: opcionesSiNo(2) },
    { clave: 'cambiosFrecuentesRopa', etiqueta: 'Cambios frecuentes de ropa, por lo menos una vez cada que cambian de turno o cuidado intensivo de heridas', opciones: opcionesSiNo(1) },
    { clave: 'laboratorioHabitual', etiqueta: 'Laboratorio, determinaciones bioquímicas y microbiológicas habituales', opciones: opcionesSiNo(1) },
    { clave: 'cambiosRopaRutina', etiqueta: 'Cambios de ropa rutina, cuidado y prevención de úlceras de decúbito', opciones: opcionesSiNo(1) },
  ],
  rangos: [],
};

export const DEFINICION_TISS_APOYO_VENTILATORIO_115: DefinicionEscalaClinica = {
  items: [
    { clave: 'asistenciaVentilatoriaMecanica', etiqueta: 'Asistencia ventilatoria mecánica, cualquier tipo de PEEP o sin ella, con o sin relajante muscular', opciones: opcionesSiNo(5) },
    { clave: 'apoyoVentilatorioSuplementario', etiqueta: 'Apoyo ventilatorio suplementario (respirar espontáneamente a través del tubo traqueal sin PEEP/O2 por cualquier método, excepto si se aplican parámetros de ventilación mecánica)', opciones: opcionesSiNo(2) },
    { clave: 'cuidadoViaAereaArtificial', etiqueta: 'Cuidado de vía aérea artificial: tubo endotraqueal o traqueostomía', opciones: opcionesSiNo(1) },
    { clave: 'tratamientoFuncionPulmonar', etiqueta: 'Tratamiento para mejorar la función pulmonar: fisioterapia, incentivo respiratorio, aspiración por el tubo endotraqueal, terapia inhalatoria', opciones: opcionesSiNo(1) },
  ],
  rangos: [],
};

export const DEFINICION_TISS_MONITORIO_115: DefinicionEscalaClinica = {
  items: [
    { clave: 'monitoreoAuriculaIzquierda', etiqueta: 'Monitoreo de aurícula izquierda', opciones: opcionesSiNo(8) },
    { clave: 'lineaArterial', etiqueta: 'Línea arterial', opciones: opcionesSiNo(5) },
    { clave: 'multiplesDrogasVasoactivas', etiqueta: 'Múltiples drogas vasoactivas, más de una droga, independientemente del tipo de drogas', opciones: opcionesSiNo(4) },
    { clave: 'reemplazoIntravenosoGrandesCantidades', etiqueta: 'Reemplazo intravenoso de grandes cantidades de líquidos, mayor 3L/m2 por día', opciones: opcionesSiNo(4) },
    { clave: 'resucitacionCardioPulmonar', etiqueta: 'Resucitación cardio pulmonar en las últimas 24 horas', opciones: opcionesSiNo(3) },
    { clave: 'unaSolaDrogaVasoactiva', etiqueta: 'Una sola droga vasoactiva (excluye múltiples vasoactivas)', opciones: opcionesSiNo(3) },
    { clave: 'cateterVenosoCentral', etiqueta: 'Catéter venoso central', opciones: opcionesSiNo(2) },
  ],
  rangos: [],
};

export const DEFINICION_TISS_APOYO_RENAL_115: DefinicionEscalaClinica = {
  items: [
    { clave: 'hemofiltracionHemodialisis', etiqueta: 'Técnicas de hemofiltración o hemodiálisis', opciones: opcionesSiNo(3) },
    { clave: 'diuresisActiva', etiqueta: 'Diuresis activa, furosemida mayor 0,5 mg/kg/d', opciones: opcionesSiNo(3) },
    { clave: 'medicionCuantitativaDiuresis', etiqueta: 'Medición cuantitativa de la diuresis', opciones: opcionesSiNo(2) },
  ],
  rangos: [],
};

export const DEFINICION_TISS_APOYO_NEUROLOGICO_115: DefinicionEscalaClinica = {
  items: [
    { clave: 'medicionPresionIntracraneana', etiqueta: 'Medición de la presión intracraneana', opciones: opcionesSiNo(4) },
    { clave: 'escalaGlasgowTiss', etiqueta: 'Escala de Glasgow', opciones: opcionesSiNo(4) },
  ],
  rangos: [],
};

export const DEFINICION_TISS_APOYO_METABOLICO_115: DefinicionEscalaClinica = {
  items: [
    { clave: 'tratamientoComplicacionesMetabolicas', etiqueta: 'Tratamiento de las complicaciones metabólicas: acidosis/alcalosis', opciones: opcionesSiNo(4) },
    { clave: 'alimentacionParenteral', etiqueta: 'Alimentación parenteral', opciones: opcionesSiNo(3) },
    { clave: 'alimentacionEnteral', etiqueta: 'Alimentación enteral a través de SNG u otra vía gastrointestinal', opciones: opcionesSiNo(2) },
  ],
  rangos: [],
};

export const DEFINICION_TISS_INTERVENCIONES_ESPECIFICAS_115: DefinicionEscalaClinica = {
  items: [
    { clave: 'intervencionesFueraUci', etiqueta: 'Intervenciones específicas fuera de la Unidad de Cuidados Intensivos: procedimiento diagnóstico o cirugías no de urgencia', opciones: opcionesSiNo(5) },
    { clave: 'multiplesIntervencionesUci', etiqueta: 'Múltiples intervenciones en la Unidad de Cuidados Intensivos (que no se describen, más de una de las que constan en las siguientes casillas)', opciones: opcionesSiNo(5) },
    { clave: 'intervencionesUnicasUci', etiqueta: 'Intervenciones específicas únicas en UCI: intubación naso u orotraqueal, colocación de marcapasos, cardioversión, endoscopía, cirugías de urgencia en las últimas 24 horas, lavado gástrico', opciones: opcionesSiNo(3) },
  ],
  rangos: [],
};

export interface GrupoTiss28_115 {
  readonly clave: string;
  readonly etiqueta: string;
  readonly definicion: DefinicionEscalaClinica;
}

export const GRUPOS_TISS28_115: readonly GrupoTiss28_115[] = [
  { clave: 'actividadesBasicas', etiqueta: 'Actividades básicas', definicion: DEFINICION_TISS_ACTIVIDADES_BASICAS_115 },
  { clave: 'apoyoVentilatorio', etiqueta: 'Apoyo ventilatorio', definicion: DEFINICION_TISS_APOYO_VENTILATORIO_115 },
  { clave: 'monitorio', etiqueta: 'Monitorio', definicion: DEFINICION_TISS_MONITORIO_115 },
  { clave: 'apoyoRenal', etiqueta: 'Apoyo renal', definicion: DEFINICION_TISS_APOYO_RENAL_115 },
  { clave: 'apoyoNeurologico', etiqueta: 'Apoyo neurológico', definicion: DEFINICION_TISS_APOYO_NEUROLOGICO_115 },
  { clave: 'apoyoMetabolico', etiqueta: 'Apoyo metabólico', definicion: DEFINICION_TISS_APOYO_METABOLICO_115 },
  { clave: 'intervencionesEspecificas', etiqueta: 'Intervenciones específicas', definicion: DEFINICION_TISS_INTERVENCIONES_ESPECIFICAS_115 },
];

export type EstadoTiss28_115 = Readonly<Record<string, RespuestasEscalaClinica>>;

export function estadoInicialTiss28_115(): EstadoTiss28_115 {
  return Object.fromEntries(GRUPOS_TISS28_115.map((g) => [g.clave, {}]));
}

export interface ClasificacionTiss28_115 {
  readonly grado: 'I' | 'II' | 'III' | 'IV';
  readonly clasificacion: string;
  readonly relacionEnfermeraPaciente: string;
}

/** Bandas de clasificación TISS-28 transcritas del papel — la relación enfermera-paciente del grado IV se transcribe tal cual la imprime el PDF fuente ("1:1 a 1:2:1"). */
const BANDAS_TISS28_115: readonly { min: number; max: number; resultado: ClasificacionTiss28_115 }[] = [
  { min: 0, max: 9, resultado: { grado: 'I', clasificacion: 'Observación', relacionEnfermeraPaciente: '1:04' } },
  { min: 10, max: 19, resultado: { grado: 'II', clasificacion: 'Vigilancia activa', relacionEnfermeraPaciente: '1:04' } },
  { min: 20, max: 39, resultado: { grado: 'III', clasificacion: 'Vigilancia intensiva', relacionEnfermeraPaciente: '1:02' } },
  { min: 40, max: Number.POSITIVE_INFINITY, resultado: { grado: 'IV', clasificacion: 'Terapéutica intensiva', relacionEnfermeraPaciente: '1:1 a 1:2:1' } },
];

/** Suma los 7 subtotales de grupo — `undefined` si falta responder cualquier ítem de cualquier grupo (mismo criterio que Glasgow: nunca un total parcial). Pura, exportada para test. */
export function totalTiss28_115(estado: EstadoTiss28_115): number | undefined {
  const subtotales = GRUPOS_TISS28_115.map((g) => calcularTotalEscala(g.definicion, estado[g.clave] ?? {}));
  if (subtotales.some((s) => s === undefined)) return undefined;
  return (subtotales as number[]).reduce((a, b) => a + b, 0);
}

/** Clasificación (grado/relación enfermera-paciente) del total TISS-28 — `undefined` si no hay total. Pura, exportada para test. */
export function clasificacionTiss28_115(total: number | undefined): ClasificacionTiss28_115 | undefined {
  if (total === undefined) return undefined;
  return BANDAS_TISS28_115.find((b) => total >= b.min && total <= b.max)?.resultado;
}

function paresDeTiss28_115(t: Traducir, estado: EstadoTiss28_115): Par[] {
  const pares: Par[] = GRUPOS_TISS28_115.flatMap((g) => paresDeEscalaClinica(g.definicion, estado[g.clave] ?? {}, `${t('formularios.f115.tiss28Titulo')} — ${g.etiqueta} — Subtotal`));
  const total = totalTiss28_115(estado);
  const clasificacion = clasificacionTiss28_115(total);
  if (total !== undefined && clasificacion !== undefined) {
    pares.push({
      pregunta: t('formularios.f115.tiss28Titulo'),
      respuesta: `${total} — Grado ${clasificacion.grado}, ${clasificacion.clasificacion} (relación enfermera-paciente ${clasificacion.relacionEnfermeraPaciente})`,
    });
  }
  return pares;
}

// ---------- Prevención de infecciones asociadas a la atención de salud (Tanda 9c) ----------
// Checklist booleano simple (cumplido/no cumplido por ítem) — a diferencia
// de `ChecklistTurno` (frecuencia+AM/PM/HS), el papel aquí solo pide una
// marca por ítem, sin turno. No se fuerza el reuso de `ChecklistTurno` para
// una forma que no encaja (equivalencia funcional, no facsímil).

export interface ItemPrevencionInfecciones115 {
  readonly clave: string;
  readonly etiqueta: string;
}

export const ITEMS_NEUMONIA_VENTILACION_115: readonly ItemPrevencionInfecciones115[] = [
  { clave: 'elevacionCabecera', etiqueta: 'Elevación de la cabecera 30° - 45°' },
  { clave: 'interrupcionVentilacionEnsayo', etiqueta: 'Interrupción una vez al día de la ventilación mecánica para ensayo de respiración espontánea' },
  { clave: 'interrupcionSedacionEvaluacionExtubacion', etiqueta: 'Interrupción diaria de la sedación y evaluación diaria de la posibilidad de extubación' },
  { clave: 'cuidadoOralClorhexidina', etiqueta: 'Cuidado oral cada 8 horas con solución de clorhexidina al 0,12 y/o cloruro de sodio al 0,9% de acuerdo a la edad' },
  { clave: 'revisionCircuitoVentilacion', etiqueta: 'Revisión del circuito de ventilación mecánica' },
  { clave: 'higieneManosGuantesAspirar', etiqueta: 'Higiene de manos y uso de guantes estériles para aspirar secreciones' },
];

export const ITEMS_CATETER_URINARIO_115: readonly ItemPrevencionInfecciones115[] = [
  { clave: 'evaluacionNecesidadCateterUrinario', etiqueta: 'Evaluación de la necesidad del catéter urinario y su extracción inmediata de ser el caso' },
  { clave: 'higieneManosManipulacion', etiqueta: 'Higiene de manos para manipulación' },
  { clave: 'sistemaDrenajeEsteril', etiqueta: 'Se mantiene un sistema de drenaje estéril, manualmente cerrado' },
  { clave: 'fundaRecolectoraDebajoVejiga', etiqueta: 'Se encuentra la funda recolectora por debajo del nivel de la vejiga sin tocar el suelo' },
  { clave: 'tecnicaAsepticaCuidado', etiqueta: 'Uso de técnica aséptica durante el cuidado: manipulación con guantes limpios, adecuada fijación, mantener el circuito permeable' },
];

export const ITEMS_DISPOSITIVOS_VASCULARES_115: readonly ItemPrevencionInfecciones115[] = [
  { clave: 'tecnicaEsterilColocacionCateterVenosoCentral', etiqueta: 'Técnica estéril durante la colocación del catéter venoso central' },
  { clave: 'evaluacionNecesidadViaCentral', etiqueta: 'Evaluación de la necesidad de la vía central' },
  { clave: 'lavadoManosManipulacionCateter', etiqueta: 'Lavado de manos antes y después de la manipulación del catéter' },
  { clave: 'desinfeccionCateterPuertosInyeccion', etiqueta: 'Desinfección del catéter y los puertos de inyección antes de acceder al catéter con alcohol al 70%' },
  { clave: 'revisionCambioApositos', etiqueta: 'Revisión y cambio de apósitos que estén húmedos, manchados o desprendidos con técnica estéril' },
];

export type EstadoPrevencionInfecciones115 = Readonly<Record<string, boolean>>;

export function estadoInicialPrevencionInfecciones115(): EstadoPrevencionInfecciones115 {
  const items = [...ITEMS_NEUMONIA_VENTILACION_115, ...ITEMS_CATETER_URINARIO_115, ...ITEMS_DISPOSITIVOS_VASCULARES_115];
  return Object.fromEntries(items.map((i) => [i.clave, false]));
}

function paresDePrevencionInfecciones115(items: readonly ItemPrevencionInfecciones115[], estado: EstadoPrevencionInfecciones115): Par[] {
  return items.filter((i) => estado[i.clave]).map((i) => ({ pregunta: i.etiqueta, respuesta: 'Cumplido' }));
}

// ---------- Procedimientos (Tanda 9c) ----------

export const FILAS_PROCEDIMIENTOS_115: readonly FilaTablaConColumnas[] = [
  { clave: 'canalizacionPeriferica', etiqueta: 'Canalización periférica' },
  { clave: 'cateterSubclavia', etiqueta: 'Catéter subclavia' },
  { clave: 'cateterYugular', etiqueta: 'Catéter yugular' },
  { clave: 'cateterFemoral', etiqueta: 'Catéter femoral' },
  { clave: 'cateterArterial', etiqueta: 'Catéter arterial' },
  { clave: 'cateterSwanGanz', etiqueta: 'Catéter Swan Ganz' },
  { clave: 'intubacionEndotraqueal', etiqueta: 'Intubación endotraqueal' },
  { clave: 'traqueostomo', etiqueta: 'Traqueostomo' },
  { clave: 'sondaNasogastricaOrogastrica', etiqueta: 'Sonda nasogástrica / orogástrica' },
  { clave: 'sondaVesical', etiqueta: 'Sonda vesical' },
  { clave: 'tuboToraxico', etiqueta: 'Tubo torácico' },
  { clave: 'puncionLumbar', etiqueta: 'Punción lumbar' },
  { clave: 'cateterDialisis', etiqueta: 'Catéter diálisis' },
  { clave: 'cateterPresionIntracraneal', etiqueta: 'Catéter de presión intracraneal' },
];

/** Renglón en blanco al final de la tabla de procedimientos del papel (2 para no quedarse corto): el nombre se escribe en la primera columna. */
export const FILAS_PROCEDIMIENTOS_OTROS_115: readonly FilaTablaConColumnas[] = [
  { clave: 'otro1', etiqueta: '1' },
  { clave: 'otro2', etiqueta: '2' },
];

export const COLUMNAS_PROCEDIMIENTOS_115: readonly ColumnaTablaConColumnas[] = [
  { clave: 'traeAlIngreso', etiqueta: 'Trae al ingreso', tipo: 'fecha' },
  { clave: 'inicio', etiqueta: 'Inicio', tipo: 'fecha' },
  { clave: 'cambio', etiqueta: 'Cambio', tipo: 'fecha' },
  { clave: 'retiro', etiqueta: 'Retiro', tipo: 'fecha' },
  { clave: 'diasDispositivo', etiqueta: 'Días dispositivo' },
];

export const COLUMNAS_PROCEDIMIENTOS_OTROS_115: readonly ColumnaTablaConColumnas[] = [{ clave: 'nombre', etiqueta: 'Otro procedimiento' }, ...COLUMNAS_PROCEDIMIENTOS_115];

// ---------- Escala del dolor en el paciente sedado (Tanda 9c) ----------

export const DEFINICION_DOLOR_SEDADO_115: DefinicionEscalaClinica = {
  items: [
    {
      clave: 'cara',
      etiqueta: 'Cara',
      opciones: [
        { valor: 0, etiqueta: 'Sonríe' },
        { valor: 1, etiqueta: 'Ceñudo' },
        { valor: 3, etiqueta: 'Trismus / Temblor' },
      ],
    },
    {
      clave: 'piernas',
      etiqueta: 'Piernas',
      opciones: [
        { valor: 0, etiqueta: 'Relajada' },
        { valor: 1, etiqueta: 'Inquieto' },
        { valor: 3, etiqueta: 'Patadas' },
      ],
    },
    {
      clave: 'actividad',
      etiqueta: 'Actividad',
      opciones: [
        { valor: 0, etiqueta: 'Quieto' },
        { valor: 1, etiqueta: 'Retorciendo' },
        { valor: 3, etiqueta: 'Arqueado / Rígido' },
      ],
    },
    {
      clave: 'llanto',
      etiqueta: 'Llanto',
      opciones: [
        { valor: 0, etiqueta: 'No' },
        { valor: 1, etiqueta: 'Gemidos' },
        { valor: 3, etiqueta: 'Chillidos' },
      ],
    },
    {
      clave: 'consuelo',
      etiqueta: 'Consuelo',
      opciones: [
        { valor: 0, etiqueta: 'Contento' },
        { valor: 1, etiqueta: 'Se distrae' },
        { valor: 3, etiqueta: 'Difícil de consolar' },
      ],
    },
  ],
  rangos: [
    { min: 0, max: 0, etiqueta: 'No dolor' },
    { min: 1, max: 2, etiqueta: 'Dolor leve' },
    { min: 3, max: 5, etiqueta: 'Dolor moderado' },
    { min: 6, max: 8, etiqueta: 'Dolor intenso' },
    { min: 9, max: 10, etiqueta: 'Máximo dolor imaginable' },
  ],
};

// ---------- Responsable de enfermería (Tanda 9c) ----------

export interface EstadoResponsableEnfermeria115 {
  readonly am: string;
  readonly pm: string;
  readonly hs: string;
}

export function estadoInicialResponsableEnfermeria115(): EstadoResponsableEnfermeria115 {
  return { am: '', pm: '', hs: '' };
}

function paresDeResponsableEnfermeria115(t: Traducir, e: EstadoResponsableEnfermeria115): Par[] {
  return [
    { pregunta: t('formularios.f115.responsableEnfermeria.am'), respuesta: e.am.trim() },
    { pregunta: t('formularios.f115.responsableEnfermeria.pm'), respuesta: e.pm.trim() },
    { pregunta: t('formularios.f115.responsableEnfermeria.hs'), respuesta: e.hs.trim() },
  ];
}

// ---------- Estado combinado ----------

export interface EstadoBitacora115 {
  readonly antropometria: EstadoAntropometria115;
  readonly datosBitacora: EstadoDatosBitacora115;
  readonly diagnostico: DiagnosticoCie10Valor;
  readonly graficoConstantes: PuntosGraficoEnfermeria;
  readonly hemodinamia: EstadoTablaHoraria24h;
  readonly grupos: GruposNombrables115;
  readonly ingresos: EstadoTablaHoraria24h;
  readonly perdidas: EstadoTablaHoraria24h;
  readonly balanceUrinario: EstadoBalanceUrinario115;
  readonly totales: EstadoTotales115;
  readonly totalDia: EstadoTotalDia115;
  readonly ulceras: EstadoTablaConColumnas;
  /** Claves `<vista>:<región>` marcadas en la silueta corporal de las úlceras/quemaduras. */
  readonly siluetaUlceras: readonly string[];
  readonly bradenBergstom: RespuestasEscalaClinica;
  readonly ramsay: RespuestasEscalaClinica;
  readonly evans: RespuestasEscalaClinica;
  readonly planCuidadosGenerales: EstadoChecklistTurno;
  readonly examenesEspeciales: EstadoTablaConColumnas;
  readonly interconsultas: EstadoTablaConColumnas;
  readonly ventilacionAltaFrecuencia: EstadoTablaHoraria24h;
  readonly ventilacionConvencional: EstadoTablaHoraria24h;
  readonly neurologico: EstadoTablaHoraria24h;
  readonly pupilas: EstadoTablaHoraria24h;
  readonly laboratorio: EstadoTablaHoraria24h;
  readonly gasometria: EstadoTablaHoraria24h;
  readonly tiss28: EstadoTiss28_115;
  readonly prevencionInfecciones: EstadoPrevencionInfecciones115;
  readonly procedimientos: EstadoTablaConColumnas;
  readonly procedimientosOtros: EstadoTablaConColumnas;
  readonly dolorSedado: RespuestasEscalaClinica;
  readonly responsableEnfermeria: EstadoResponsableEnfermeria115;
}

export function estadoInicialBitacora115(): EstadoBitacora115 {
  return {
    antropometria: estadoInicialAntropometria115(),
    datosBitacora: estadoInicialDatosBitacora115(),
    diagnostico: { texto: '', cie10: null },
    graficoConstantes: estadoInicialGraficoEnfermeria(SERIES_GRAFICO_CONSTANTES_115),
    hemodinamia: estadoInicialTablaHoraria24h(FILAS_HEMODINAMIA_115),
    grupos: estadoInicialGruposNombrables115(),
    ingresos: estadoInicialTablaHoraria24h(FILAS_INGRESOS_115),
    perdidas: estadoInicialTablaHoraria24h(FILAS_PERDIDAS_115),
    balanceUrinario: estadoInicialBalanceUrinario115(),
    totales: estadoInicialTotales115(),
    totalDia: estadoInicialTotalDia115(),
    ulceras: estadoInicialTablaConColumnas(FILAS_ULCERAS_115, COLUMNAS_ULCERAS_115),
    siluetaUlceras: [],
    bradenBergstom: {},
    ramsay: {},
    evans: {},
    planCuidadosGenerales: estadoInicialChecklistTurno(ITEMS_PLAN_CUIDADOS_GENERALES_115),
    examenesEspeciales: estadoInicialTablaConColumnas(FILAS_EXAMENES_ESPECIALES_115, COLUMNAS_EXAMENES_ESPECIALES_115),
    interconsultas: estadoInicialTablaConColumnas(FILAS_INTERCONSULTAS_115, COLUMNAS_INTERCONSULTAS_115),
    ventilacionAltaFrecuencia: estadoInicialTablaHoraria24h(FILAS_VENTILACION_ALTA_FRECUENCIA_115),
    ventilacionConvencional: estadoInicialTablaHoraria24h(FILAS_VENTILACION_CONVENCIONAL_115),
    neurologico: estadoInicialTablaHoraria24h(FILAS_NEUROLOGICO_115),
    pupilas: estadoInicialTablaHoraria24h(FILAS_PUPILAS_115),
    laboratorio: estadoInicialTablaHoraria24h(FILAS_LABORATORIO_115),
    gasometria: estadoInicialTablaHoraria24h(FILAS_GASOMETRIA_115),
    tiss28: estadoInicialTiss28_115(),
    prevencionInfecciones: estadoInicialPrevencionInfecciones115(),
    procedimientos: estadoInicialTablaConColumnas(FILAS_PROCEDIMIENTOS_115, COLUMNAS_PROCEDIMIENTOS_115),
    procedimientosOtros: estadoInicialTablaConColumnas(FILAS_PROCEDIMIENTOS_OTROS_115, COLUMNAS_PROCEDIMIENTOS_OTROS_115),
    dolorSedado: {},
    responsableEnfermeria: estadoInicialResponsableEnfermeria115(),
  };
}

function paresDeFilasHorarias(filas: readonly FilaTablaHoraria24h[], estado: EstadoTablaHoraria24h): Par[] {
  return filas
    .map((f) => ({ pregunta: f.etiqueta, respuesta: resumenFilaTablaHoraria24h(estado, f.clave) }))
    .filter((p) => p.respuesta !== '');
}

function paresDeFilasConColumnas(filas: readonly FilaTablaConColumnas[], columnas: readonly ColumnaTablaConColumnas[], estado: EstadoTablaConColumnas, prefijoEtiqueta: string): Par[] {
  return filas
    .map((f) => ({ pregunta: `${prefijoEtiqueta} ${f.etiqueta}`, respuesta: resumenFilaTablaConColumnas(estado, f.clave, columnas) }))
    .filter((p) => p.respuesta !== '');
}

/** "Otros" procedimientos: "<Procedimientos> — <nombre>" con sus fechas; un renglón sin nombre sale numerado. */
function paresDeProcedimientosOtros115(t: Traducir, estado: EstadoTablaConColumnas): Par[] {
  const titulo = t('formularios.f115.procedimientosTitulo');
  return FILAS_PROCEDIMIENTOS_OTROS_115.flatMap((f) => {
    const nombre = (estado[f.clave]?.nombre ?? '').trim();
    const datos = resumenFilaTablaConColumnas(estado, f.clave, COLUMNAS_PROCEDIMIENTOS_115);
    if (nombre === '' && datos === '') return [];
    return [{ pregunta: `${titulo} — ${nombre !== '' ? nombre : `${t('formularios.uci.textosFijos.otroRenglon')} ${f.etiqueta}`}`, respuesta: datos || t('formularios.uci.textosFijos.sinDatos') }];
  });
}

/** Serializa toda la bitácora de 115 a `Par[]`, listos para `onGuardado` — filtra respuestas vacías. */
export function paresDeBitacora115(t: Traducir, estado: EstadoBitacora115): Par[] {
  const pares: Par[] = [
    ...paresDeAntropometria115(t, estado.antropometria),
    ...paresDeDatosBitacora115(t, estado.datosBitacora),
    { pregunta: t('formularios.f115.diagnosticoIngreso'), respuesta: conCodigoCie(estado.diagnostico) },
    ...paresDeFilasHorarias(FILAS_HEMODINAMIA_115, estado.hemodinamia),
    ...paresDeFilasNombrables(t('formularios.f115.medicacionTitulo'), estado.grupos.medicacion, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasNombrables(t('formularios.f115.nebulizacionTitulo'), estado.grupos.nebulizacion, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasNombrables(t('formularios.f115.ingresosLibresTitulo'), estado.grupos.ingresosLibres, t('formularios.uci.textosFijos.sinRegistroHorario')),
    ...paresDeFilasHorarias(FILAS_INGRESOS_115, estado.ingresos),
    ...paresDeFilasHorarias(FILAS_PERDIDAS_115, estado.perdidas),
    ...paresDeBalanceUrinario115(t, estado.balanceUrinario),
    ...paresDeTotales115(t, estado.totales),
    ...paresDeTotalDia115(t, estado.totalDia, estado.ingresos, estado.perdidas, estado.grupos.ingresosLibres),
    ...(estado.siluetaUlceras.length > 0 ? [{ pregunta: t('formularios.f115.siluetaRegionesMarcadas'), respuesta: resumenSilueta(estado.siluetaUlceras) }] : []),
    ...paresDeFilasConColumnas(FILAS_ULCERAS_115, COLUMNAS_ULCERAS_115, estado.ulceras, t('formularios.f115.ulcerasTitulo')),
    ...paresDeEscalaClinica(DEFINICION_BRADEN_BERGSTOM_115, estado.bradenBergstom, t('formularios.f115.bradenBergstomTitulo')),
    ...paresDeEscalaClinica(DEFINICION_RAMSAY_115, estado.ramsay, t('formularios.f115.ramsayTitulo')),
    ...paresDeEscalaClinica(DEFINICION_EVANS_115, estado.evans, t('formularios.f115.evansTitulo')),
    ...paresDeItemsChecklistTurno(ITEMS_PLAN_CUIDADOS_GENERALES_115, estado.planCuidadosGenerales),
    ...paresDeFilasConColumnas(FILAS_EXAMENES_ESPECIALES_115, COLUMNAS_EXAMENES_ESPECIALES_115, estado.examenesEspeciales, t('formularios.f115.examenesEspecialesTitulo')),
    ...paresDeFilasConColumnas(FILAS_INTERCONSULTAS_115, COLUMNAS_INTERCONSULTAS_115, estado.interconsultas, t('formularios.f115.interconsultasTitulo')),
    ...paresDeFilasHorarias(FILAS_VENTILACION_ALTA_FRECUENCIA_115, estado.ventilacionAltaFrecuencia),
    ...paresDeFilasHorarias(FILAS_VENTILACION_CONVENCIONAL_115, estado.ventilacionConvencional),
    ...paresDeFilasHorarias(FILAS_NEUROLOGICO_115, estado.neurologico),
    ...(resumenTotalGlasgowPorHora115(estado.neurologico) !== '' ? [{ pregunta: t('formularios.f115.neurologico.totalGlasgow'), respuesta: resumenTotalGlasgowPorHora115(estado.neurologico) }] : []),
    ...paresDeFilasHorarias(FILAS_PUPILAS_115, estado.pupilas),
    ...paresDeFilasHorarias(FILAS_LABORATORIO_115, estado.laboratorio),
    ...paresDeFilasHorarias(FILAS_GASOMETRIA_115, estado.gasometria),
    ...paresDeTiss28_115(t, estado.tiss28),
    ...paresDePrevencionInfecciones115(ITEMS_NEUMONIA_VENTILACION_115, estado.prevencionInfecciones),
    ...paresDePrevencionInfecciones115(ITEMS_CATETER_URINARIO_115, estado.prevencionInfecciones),
    ...paresDePrevencionInfecciones115(ITEMS_DISPOSITIVOS_VASCULARES_115, estado.prevencionInfecciones),
    ...paresDeFilasConColumnas(FILAS_PROCEDIMIENTOS_115, COLUMNAS_PROCEDIMIENTOS_115, estado.procedimientos, t('formularios.f115.procedimientosTitulo')),
    ...paresDeProcedimientosOtros115(t, estado.procedimientosOtros),
    ...paresDeEscalaClinica(DEFINICION_DOLOR_SEDADO_115, estado.dolorSedado, t('formularios.f115.dolorSedadoTitulo')),
    ...paresDeResponsableEnfermeria115(t, estado.responsableEnfermeria),
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
